export const namespaced = true;

import BillingService from '@/services/BillingService.js';
import sumBy from 'lodash/sumBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import pick from 'lodash/pick';
import {
  basicPresent,
  basicBillingTypeDisplay,
  basicBillingTypeIdDisplay,
  basicMoneyAmount,
  basicSubFieldPresent,
  basicJapaneseYearMonth,
  basicBillingTeachingMaterial400Display,
  basicBillingTeachingMaterial200Display,
  basicBillingTeachingMaterial100Display,
  basicBillingTeachingMaterial300Display,
  basicBillingTeachingMaterial700Display,
  basicBillingTeachingMaterial600Display,
  basicBillingTeachingMaterial500Display,
} from '@/presenters/fields';
import { dateDiff } from '@/utils/year_helpers';

export const state = {
  billings: [],
  billingDetails: [],
  billingDate: {},
  billingTotal: 0,
  exportDate: {},
  billingExcel: null,
  updateDate: null,
};

export const mutations = {
  SET_BILLINGS(state, billings) {
    state.billings = billings;
  },
  APPEND_BILLINGS(state, billings) {
    state.billings = state.billings.concat(billings);
  },
  SET_BILLING_DATE(state, date) {
    state.billingDate = date;
  },
  CLEAR_BILLINGS(state) {
    state.billings = [];
    state.billingTotal = 0;
    state.billingExcel = null;
    state.updateDate = null;
  },
  SET_BILLING_DETAILS(state, billingDetails) {
    state.billingDetails = billingDetails;
  },
  SET_BILLING_TOTAL(state, total) {
    state.billingTotal = total;
  },
  SET_EXPORT_DATE(state, exportDate) {
    state.exportDate = exportDate;
  },
  SET_BILLING_EXCEL(state, excel) {
    state.billingExcel = excel;
  },
  SET_UPDATE_DATE(state, updateDate) {
    state.updateDate = updateDate;
  },
};

export const actions = {
  fetchBillings({ commit }, { data }) {
    return BillingService.all(data).then((response) => {
      //TODO: Must be a better solution if we correct the date in response.data.date
      const billingDate = { year: data.year, month: data.month, day: data.day };
      commit('SET_BILLING_DATE', billingDate);
      commit('SET_EXPORT_DATE', response.data.exportDate);

      const flatBillings = response.data.items.flat();
      for (let index = 0; index < flatBillings.length; index++) {
        const billing = flatBillings[index];
        billing['no'] = index + 1;
      }
      commit('SET_BILLINGS', flatBillings);
    });
  },
  fetchAndAppendBillings({ commit, state }, { data }) {
    return BillingService.all(data).then((response) => {
      //TODO: Must be a better solution if we correct the date in response.data.date
      const billingDate = { year: data.year, month: data.month, day: data.day };
      commit('SET_BILLING_DATE', billingDate);
      commit('SET_BILLING_TOTAL', response.data.total);

      const originalExportDate = {
        year: parseInt(state.exportDate.year, 10),
        month: parseInt(state.exportDate.month, 10) - 1,
        day: parseInt(state.exportDate.day, 10),
      };
      const newExportDate = {
        year: parseInt(response.data.exportDate.year, 10),
        month: parseInt(response.data.exportDate.month, 10) - 1,
        day: parseInt(response.data.exportDate.day, 10),
      };

      if (Object.keys(state.exportDate).length === 0) {
        commit('SET_EXPORT_DATE', response.data.exportDate);
      } else if (dateDiff(originalExportDate, newExportDate) > 0) {
        commit('SET_EXPORT_DATE', response.data.exportDate);
      }

      const baseIndex = state.billings.length;
      const flatBillings = response.data.items.flat();
      for (let index = 0; index < flatBillings.length; index++) {
        const billing = flatBillings[index];
        billing['no'] = baseIndex + index + 1;
      }
      commit('APPEND_BILLINGS', flatBillings);
    });
  },
  fetchExcel({ commit }, { data }) {
    return BillingService.fetchExcel(data).then((response) => {
      commit('SET_BILLING_EXCEL', response.data.excel);
      commit('SET_UPDATE_DATE', response.data.updateDate);
    });
  },
  callExcelGeneration(context, { data }) {
    return BillingService.callExcelGeneration(data);
  },
  clearBillings({ commit }) {
    commit('CLEAR_BILLINGS');
  },
  selectBillingDetails({ commit }, billings) {
    let billingDetails = billings;
    commit('SET_BILLING_DETAILS', billingDetails);
  },
  updateBillingStatus({ commit, state }, { data }) {
    data.billingDate = state.billingDate;

    return BillingService.updateStatus(data).then(() => {
      const billingDetails = map(state.billingDetails, (billing) => {
        if (billing.id == data.id) {
          billing.status = data.status;
        }

        return billing;
      });

      commit('SET_BILLING_DETAILS', billingDetails);
    });
  },
};

export const getters = {
  getBillingsByBillingId: (state) => (billingId) => {
    return state.billings.filter((item) => item.billingId == billingId);
  },
  getBillingByNo: (state) => (no) => {
    return state.billings.find((item) => item.no == no);
  },
  getBillingDetailById: (state) => (id) => {
    return state.billingDetails.find((item) => item.id == id);
  },
  totalBillings: (state) => {
    return state.billings.length;
  },
  totalBillingIds: (state) => {
    return uniq(map(state.billings, (billing) => billing.billingId)).length;
  },
  totalBillingsToLoadMore: (state) => {
    return state.billingTotal;
  },
  totalBillingAmount: (state) => {
    return sumBy(state.billings, 'totalPrice');
  },
  totalVATAmount: (state, getters) => {
    return getters.totalBillingAmount * 0.1;
  },
  totalBillingAfterVATAmount: (state, getters) => {
    return getters.totalBillingAmount + getters.totalVATAmount;
  },
  totalBillingDetails: (state) => {
    return state.billingDetails.length;
  },
  canLoadMoreBillings: (state, getters) => {
    return getters.totalBillingIds < state.billingTotal;
  },
  getExcelFormattedBillings: (state, getters) => {
    let allBillings = map(state.billings, (billing) => {
      return {
        No: basicPresent(billing.no).toString(),
        区分: basicBillingTypeDisplay(billing.billingType),
        ID名: basicPresent(billing.billingName),
        ID: basicPresent(billing.billingId),
        項目: basicBillingTypeIdDisplay(billing.billingTypeId),
        数量: basicMoneyAmount(billing.quantity, false).toString(),
        単位: basicPresent(billing.unit),
        単価: basicMoneyAmount(billing.price, false).toString(),
        合計: basicMoneyAmount(billing.totalPrice, false).toString(),
      };
    });
    allBillings.push({
      単価: '小計（税抜）',
      合計: basicMoneyAmount(getters.totalBillingAmount, false).toString(),
    });
    allBillings.push({
      単価: '消費税10%',
      合計: basicMoneyAmount(getters.totalVATAmount, false).toString(),
    });
    allBillings.push({
      単価: '合計金額',
      合計: basicMoneyAmount(getters.totalBillingAfterVATAmount, false).toString(),
    });
    allBillings.push({});
    allBillings.push({ No: '備考' });
    allBillings.push({
      No: '※御利用月に（自動集計）と記載がある場合、記載されている金額は実際の請求と異なる場合があります。',
    });
    allBillings.push({
      No: '※休会中の生徒は、生徒IDにはカウントされません。',
    });
    allBillings.push({
      No: '※月の途中で教場やブランドの紐づけを変更した場合、変更後の教場やブランドとしてカウントされます。',
    });
    return allBillings;
  },
  getExcelHeaderBillings: () => {
    return ['No', '区分', 'ID名', 'ID', '項目', '数量', '単位', '単価', '合計'];
  },
  getExcelHeaderHashBillings: () => {
    return {
      No: 'No',
      区分: '区分',
      ID名: 'ID名',
      ID: 'ID',
      項目: '項目',
      数量: '数量',
      単位: '単位',
      単価: '単価',
      合計: '合計',
    };
  },
  getExcelFormattedStudentBillingDetails: (state) => (fields) => {
    return map(state.billingDetails, (billingDetail) => {
      let data = {
        ブランド名: basicSubFieldPresent(billingDetail, 'brand', 'name'),
        教場名: basicSubFieldPresent(billingDetail, 'school', 'name'),
        生徒ID: basicPresent(billingDetail.studentId),
        生徒名: basicPresent(billingDetail.name),
        休会開始月: basicJapaneseYearMonth(billingDetail.deactivatedDatetime),
        教材数: basicPresent(billingDetail.numOfBooks).toString(),
        '教材（図形の極）': basicBillingTeachingMaterial400Display(billingDetail.teachingMaterials),
        '教材（数の極）': basicBillingTeachingMaterial200Display(billingDetail.teachingMaterials),
        '教材（中学生の図形の極）': basicBillingTeachingMaterial100Display(billingDetail.teachingMaterials),
        '教材（中学生の代数の極）': basicBillingTeachingMaterial300Display(billingDetail.teachingMaterials),
        '教材（国語）': basicBillingTeachingMaterial700Display(billingDetail.teachingMaterials),
        '教材（遊）': basicBillingTeachingMaterial600Display(billingDetail.teachingMaterials),
        '教材（悟）': basicBillingTeachingMaterial500Display(billingDetail.teachingMaterials),
      };

      return pick(data, fields);
    });
  },
  getExcelFormattedTeacherBillingDetails: (state) => {
    return map(state.billingDetails, (billingDetail) => {
      return {
        ブランド名: basicSubFieldPresent(billingDetail, 'brand', 'name'),
        先生ID: basicPresent(billingDetail.teacherId),
        先生名: basicPresent(billingDetail.name),
      };
    });
  },
  getExcelHeaderTeacherBillingDetails: () => {
    return ['ブランド名', '先生ID', '先生名'];
  },
};
